import {
  Consent,
  DigitalGpSubmission,
  DigitalGpSubmissionBindArgs,
  DigitalGpSubmissionBindResponse,
  DigitalGpSubmissionCalculateArgs,
  DigitalGpSubmissionCalculateResponse,
  DigitalGpSubmissionProposeArgs,
  DigitalGpSubmissionProposeResponse,
  DigitalGpSubmissionSaveResponse,
  Ids,
} from '@sky-distribution/digital-gp-submission';
import {
  getSessionId,
  logAndThrowHttpError,
} from '@sky-distribution/gp-onboarding-monitoring';
import axios, { AxiosInstance } from 'axios';
import {
  API_BASE_URL,
  ApiPath,
  PRODUCT_ID_HEADER_NAME,
  SESSION_ID_HEADER_NAME,
  TENANT_ID_HEADER_NAME,
} from '../config/config';
import { getSubmissionWithConsents } from '../util/consents';

export const getMockedResponseHeader = (): Record<string, unknown> => {
  const isMockedOn = localStorage.getItem('x-mocked-response') === 'true';

  return {
    'x-mocked-response': isMockedOn,
  };
};

const api = (): AxiosInstance => axios.create({
  baseURL: API_BASE_URL,
  headers: {
    [SESSION_ID_HEADER_NAME]: getSessionId(),
  },
});

const getGpHeaders = (productId: string, tenant: string): Record<string, string> => ({
  [PRODUCT_ID_HEADER_NAME]: productId,
  [TENANT_ID_HEADER_NAME]: tenant,
  ...getMockedResponseHeader(),
});

export const calculate = async (
  body: DigitalGpSubmissionCalculateArgs,
  productId: string,
  tenant: string,
): Promise<DigitalGpSubmissionCalculateResponse> => {
  const { data } = await api()
    .post<DigitalGpSubmissionCalculateResponse>(ApiPath.CALCULATE, body, { headers: getGpHeaders(productId, tenant) })
    .catch(logAndThrowHttpError<DigitalGpSubmissionCalculateResponse>('rate', body));

  return data;
};

export const save = async (
  submission: DigitalGpSubmission,
  productId: string,
  tenant: string,
  consentStatus: Record<string, boolean>,
  consentDictionary: Consent[],
  ids?: Ids,
): Promise<DigitalGpSubmissionSaveResponse> => {
  const hasIds = !!ids;
  const submissionWithConsents = getSubmissionWithConsents(submission, consentStatus, consentDictionary);
  const body = hasIds ? { data: submissionWithConsents, ids } : submissionWithConsents;

  const { data } = await api()
    .post<DigitalGpSubmissionSaveResponse>(ApiPath.SAVE, body, { headers: getGpHeaders(productId, tenant) })
    .catch(
      logAndThrowHttpError<DigitalGpSubmissionSaveResponse>(ApiPath.SAVE, body),
    );

  return data;
};

export const propose = async (
  body: DigitalGpSubmissionProposeArgs,
  productId: string,
  tenant: string,
): Promise<DigitalGpSubmissionProposeResponse> => {
  const { data } = await api()
    .post<DigitalGpSubmissionProposeResponse>(ApiPath.PROPOSE, body, { headers: getGpHeaders(productId, tenant) })
    .catch(logAndThrowHttpError<DigitalGpSubmissionProposeResponse>(ApiPath.PROPOSE, body));

  return data;
};

export const bind = async (
  body: DigitalGpSubmissionBindArgs,
  productId: string,
  tenant: string,
): Promise<DigitalGpSubmissionBindResponse> => {
  const { data } = await api()
    .post<DigitalGpSubmissionBindResponse>(ApiPath.BIND, body, { headers: getGpHeaders(productId, tenant) })
    .catch(logAndThrowHttpError<DigitalGpSubmissionBindResponse>(ApiPath.BIND, body));

  return data;
};
